import { Fragment, useCallback, useMemo, useState } from 'react';
import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { Header } from '@components/Header';
import JobsIcon from '@components/icons/Jobs.svg';
import MoveIcon from '@components/icons/Move.svg';
import RefundIcon from '@components/icons/Refund.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { TagOutlined } from '@components/core-ui/Tag/variations/TagOutlined';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { jobCanBeMoved } from '@helpers/jobStatus/jobCanBeMoved';
import { jobCanBeRefunded } from '@helpers/jobStatus/jobCanBeRefunded';
import { jobCanBeResubmitted } from '@helpers/jobStatus/jobCanBeResubmitted';
import { downloadBlobResponseAsFile } from '@helpers/downloadBlobResponseAsFile';
import { handleDownloadError } from '@helpers/jobLinkHelpers';
import { hasVsDeveloperRole } from '@helpers/hasVsDeveloperRole';
import { minutesToSeconds } from '@helpers/minutesToSeconds';
import { parseContentDispositionFilename } from '@helpers/parseContentDispositionFilename';
import { secondsToHHMMSS } from '@helpers/secondsToHHMMSS';
import { serializeCommaSeparatedQueryParam } from '@helpers/serializeCommaSeparatedQueryParam';
import { buildJobsFiltersSearchParams } from '@helpers/buildJobsFiltersSearchParams';
import { hasActiveJobsFilters } from '@helpers/hasActiveJobsFilters';
import { parseJobsFiltersFromSearchParams } from '@helpers/parseJobsFiltersFromSearchParams';
import { TAppliedFilters } from 'app/types/TAppliedFilters';
import { useModalContext } from '@providers/ModalProvider';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { JobReportResponseDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useExternallyManagedFilter } from 'app/hooks/useExternallyManagedFilter';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { client } from 'app/http';
import { adminJobsKeys, TJobsReportListParams } from 'app/queries/adminJobs/adminJobsKeys';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';

import { createFetchJobsReportQueryFn } from '../../api/fetchJobsReport';
import { CellContent } from './styles/CellContent';
import { CenteredPercents } from './styles/CenteredPercents';
import { FlexCellContent } from './styles/FlexCellContent';
import { StyledCircularProgress } from './styles/StyledCircularProgress';
import { StyledLink } from './styles/StyledLink';
import { StyledProgressBarTextWrapper } from './styles/StyledProgressBarTextWrapper';
import { StyledProgressBarWrapper } from './styles/StyledProgressBarWrapper';
import { Wrapper } from './styles/Wrapper';
import { JobStatusText } from '@components/JobStatusText';

const SELECT_NUMBER_OF_JOBS_LIMIT = 30;

const SEARCH_DEBOUNCE_MS = 200;

export const Content = () => {
  const { me } = useAuthContext();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { searchParams, page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize, updateParams } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const { includeExternallyManaged, toggleIncludeExternallyManaged } = useExternallyManagedFilter({ setPage });
  const canViewExternallyManaged = hasVsDeveloperRole(me?.roles);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const filters = useMemo(() => parseJobsFiltersFromSearchParams(searchParams), [searchParams]);
  const { jobsFilter, statusFilter, asOneFilter, postFilter, meetingTypeFilter, durationFilter } = filters;

  const listParams = useMemo<TJobsReportListParams>(() => {
    const fromParam = Math.floor(Number(jobsFilter.from) / 1000) || '';
    const toParam = Math.floor(Number(jobsFilter.to) / 1000) || '';
    return {
      page,
      pageSize,
      orderBy,
      orderType,
      search: debouncedSearch,
      statuses: statusFilter,
      from: `${fromParam}`,
      to: `${toParam}`,
      asOne: asOneFilter,
      post: postFilter,
      meetingTypes: meetingTypeFilter.length ? meetingTypeFilter : undefined,
      durationFrom: minutesToSeconds(durationFilter.fromMinutes),
      durationTo: minutesToSeconds(durationFilter.toMinutes),
      includeExternallyManaged,
    };
  }, [
    asOneFilter,
    debouncedSearch,
    durationFilter.fromMinutes,
    durationFilter.toMinutes,
    jobsFilter.from,
    jobsFilter.to,
    includeExternallyManaged,
    meetingTypeFilter,
    orderBy,
    orderType,
    page,
    pageSize,
    postFilter,
    statusFilter,
  ]);

  const jobsReportsQuery = useQuery({
    queryKey: adminJobsKeys.reportList(listParams),
    queryFn: createFetchJobsReportQueryFn(),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });
  const jobsReports = jobsReportsQuery.data ?? { data: [], count: 0 };
  const isLoading = jobsReportsQuery.isFetching || isSearchSettling;

  const refreshReports = () => {
    closeModal();
    void queryClient.invalidateQueries({ queryKey: adminJobsKeys.reportLists() });
  };

  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await client.get('/admin/jobs-report/export', {
        params: {
          statuses: serializeCommaSeparatedQueryParam(statusFilter),
          search: debouncedSearch,
          from: listParams.from,
          to: listParams.to,
          asOne: listParams.asOne,
          post: listParams.post,
          meetingTypes: serializeCommaSeparatedQueryParam(meetingTypeFilter),
          durationFrom: listParams.durationFrom,
          durationTo: listParams.durationTo,
          includeExternallyManaged: listParams.includeExternallyManaged,
        },
        responseType: 'blob',
      });
      const dispositionHeader = response.headers['content-disposition'];
      const parsedFilename = parseContentDispositionFilename(
        typeof dispositionHeader === 'string' ? dispositionHeader : undefined,
      );
      const filename = parsedFilename || `jobs-report-${new Date().toISOString().slice(0, 10)}.csv`;
      downloadBlobResponseAsFile(response.data, filename);
    },
    onError: (error) => {
      handleDownloadError(error);
    },
  });
  const isExporting = exportMutation.isPending;

  const exportCSV = () => {
    if (!exportMutation.isPending) {
      exportMutation.mutate();
    }
  };

  const applyModalFilters = useCallback(
    (appliedFilters: TAppliedFilters) => {
      updateParams(buildJobsFiltersSearchParams(appliedFilters), { resetPage: true });
    },
    [updateParams],
  );

  const showResubmitJobModal = (data: JobReportResponseDto['data'][0]) => {
    openModal(ModalNamesEnum.AdminResubmitJobModal, {
      onSuccess: refreshReports,
      id: data.id,
      job: data,
    });
  };

  const showMoveJobToWSModal = (jobId: string, workspaceId: string) => {
    openModal(ModalNamesEnum.AdminMoveJobModal, {
      jobId,
      workspaceId,
      onSuccess: refreshReports,
    });
  };

  const showRefundJobModal = (data: JobReportResponseDto['data'][0]) => {
    openModal(ModalNamesEnum.AdminRefundJobModal, {
      onSuccess: refreshReports,
      id: data.id,
      price: data.price,
    });
  };

  const showBulkResubmitJobsModal = (ids: string[]) => {
    openModal(ModalNamesEnum.AdminBulkResubmitJobsModal, {
      onSuccess: refreshReports,
      ids,
    });
  };

  const showCloneJobsModal = () => {
    openModal(ModalNamesEnum.AdminCloneJobModal, {
      onSuccess: refreshReports,
    });
  };
  const [chosenIds, setChosenIds] = useState<Set<string>>(new Set());

  const generateJobLink = (jobId: string) => {
    return routes.adminJobDetails.make(jobId);
  };

  const openFiltersModal = () => {
    openModal(ModalNamesEnum.AdminJobsReportFilters, {
      jobsFilter,
      statusFilter,
      asOneFilter,
      postFilter,
      meetingTypeFilter,
      durationFilter,
      onApplyFilters: applyModalFilters,
    });
  };

  return (
    <>
      {/* Export CSV temporarily hidden: the current export implementation can OOM the backend (under investigation) */}
      <Header
        title="Jobs Report"
        setSearch={setSearch}
        search={search}
        buttonLabel={jobsReports?.data?.length ? 'Export CSV' : undefined}
        buttonOnClick={jobsReports?.data?.length ? () => exportCSV() : undefined}
        buttonLoading={isExporting}
        secondButtonLabel={chosenIds.size ? 'Resubmit Selected' : 'Clone job'}
        secondButtonOnClick={() => {
          if (chosenIds.size) {
            showBulkResubmitJobsModal(Array.from(chosenIds));
            return;
          }
          showCloneJobsModal();
        }}
        onFilterClick={openFiltersModal}
        filterCounter={hasActiveJobsFilters(filters) ? 1 : 0}
      />
      <LinearProgressLoader active={isLoading} />

      <Wrapper>
        {canViewExternallyManaged ? (
          <div className="flex justify-end px-4 py-2">
            <Switch
              checked={includeExternallyManaged}
              onChange={toggleIncludeExternallyManaged}
              testId="show-externally-managed-jobs-switch"
              label="Show externally managed"
            />
          </div>
        ) : null}
        <TableWrapper>
          <Table>
            <Thead $sticky>
              <Tr>
                <Th>
                  <CheckBox
                    name={'chosenAll'}
                    onChange={() => {
                      jobsReports.data?.forEach((data) => {
                        if (chosenIds.size === SELECT_NUMBER_OF_JOBS_LIMIT) {
                          setChosenIds(new Set());
                          return;
                        }

                        setChosenIds((prev) => {
                          if (prev.size >= SELECT_NUMBER_OF_JOBS_LIMIT) {
                            return prev;
                          }
                          const next = new Set(prev);
                          if (next.has(data.id) && jobsReports.data?.every((data) => chosenIds.has(data.id))) {
                            next.delete(data.id);
                          } else {
                            next.add(data.id);
                          }
                          return next;
                        });
                      });
                    }}
                    checked={
                      jobsReports.data?.every((data) => chosenIds.has(data.id)) ||
                      chosenIds.size === SELECT_NUMBER_OF_JOBS_LIMIT ||
                      false
                    }
                  />
                </Th>
                <Th>Job Title</Th>
                <Th>Creator</Th>
                <Th>Org / WS</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>ASR used</Th>
                <Th>Price</Th>
                <Th>Refunded</Th>
                <Th style={{ width: '1%' }}>Edits %</Th>
                <Th style={{ width: '1%' }}></Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobsReports?.data?.map((data) => {
                const percent = Math.round(data.editingProgress || 0);
                const showResubmitButton = jobCanBeResubmitted(data.status);
                const showMoveButton =
                  !data.asOneLocked && jobCanBeMoved(data.status as keyof typeof JOB_STATUSES_ENUM);
                const showRefundButton =
                  (jobCanBeRefunded(data.status as keyof typeof JOB_STATUSES_ENUM) || data.partialUnlocked) &&
                  !data.refunded;
                return (
                  <Fragment key={data.id}>
                    <Tr>
                      <Td>
                        <div className="max-w-full flex flex-row items-center">
                          <CheckBox
                            name={'chosen'}
                            onChange={() => {
                              setChosenIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(data.id)) {
                                  next.delete(data.id);
                                } else {
                                  next.add(data.id);
                                }
                                return next;
                              });
                            }}
                            disabled={chosenIds.size >= SELECT_NUMBER_OF_JOBS_LIMIT && !chosenIds.has(data.id)}
                            checked={chosenIds.has(data.id)}
                          />
                        </div>
                      </Td>
                      <Td>
                        <div className="max-w-full">
                          <div className="flex flex-row items-center max-w-full">
                            {data.meetingId ? (
                              <TagAccent className="mr-2 text-[10px]">AS ONE</TagAccent>
                            ) : (
                              <TagOutlined className="mr-2 text-[10px]">POST</TagOutlined>
                            )}
                            <StyledLink
                              className="max-w-full text-ellipsis overflow-hidden"
                              to={generateJobLink(data.id)}
                            >
                              {data.jobName || 'Unnamed'}
                            </StyledLink>
                          </div>
                          <span style={{ display: 'block' }}>{data.id}</span>
                        </div>
                      </Td>
                      <Td>
                        <CellContent>
                          <span style={{ display: 'block' }}>
                            {data.name} {data.lastname}
                          </span>
                          <span style={{ display: 'block' }}>{data.email}</span>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <StyledLink to={routes.adminOrganizationDetails.make(data.organization?.id)}>
                            {data.organization?.name}
                          </StyledLink>
                          <br />
                          <StyledLink to={routes.adminWorkspaceDetails.make(data.workspace?.id)}>
                            {data.workspace?.name}
                          </StyledLink>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <JobStatusText status={data.status} removed={data.removed} />
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>{secondsToHHMMSS(data.duration ?? 0)}</CellContent>
                      </Td>
                      <Td>{data.price ? <CellContent>{formatCentsPrice(data.price ?? 0)}</CellContent> : null}</Td>
                      <Td>{data.refunded ? <CellContent>Yes</CellContent> : null}</Td>
                      <Td>
                        <CenteredPercents>
                          {JOB_STATUSES_ENUM[data.status as keyof typeof JOB_STATUSES_ENUM] ===
                          JOB_STATUSES_ENUM.JOB_STATUS_TRANSCRIBING ? (
                            <StyledProgressBarWrapper>
                              <StyledCircularProgress
                                size={20}
                                trackWidth={3}
                                indicatorWidth={3}
                                progress={percent}
                                trackColor="var(--color-tint)"
                                indicatorColor="#858dbd"
                              />
                              <StyledProgressBarTextWrapper>{percent}%</StyledProgressBarTextWrapper>
                            </StyledProgressBarWrapper>
                          ) : null}
                        </CenteredPercents>
                      </Td>
                      <Td>
                        <FlexCellContent>
                          {showMoveButton ? (
                            <IconButton
                              title="Move to another Workspace"
                              onClick={() => showMoveJobToWSModal(data.id, data.workspace?.id ?? '')}
                            >
                              <MoveIcon />
                            </IconButton>
                          ) : null}
                          {showResubmitButton ? (
                            <IconButton title="Resubmit" onClick={() => showResubmitJobModal(data)}>
                              <JobsIcon />
                            </IconButton>
                          ) : null}
                          {showRefundButton ? (
                            <IconButton title="Refund" onClick={() => showRefundJobModal(data)}>
                              <RefundIcon />
                            </IconButton>
                          ) : null}
                        </FlexCellContent>
                      </Td>
                    </Tr>
                    {data.Transactions?.length ? (
                      <Tr className="!border-t-0">
                        <Td></Td>
                        <Td colSpan={100}>
                          <div className="pb-4">
                            {data.Transactions.map((transaction) => (
                              <div key={transaction.id}>
                                <small>
                                  {transaction.code.name} {transaction.id}
                                </small>
                              </div>
                            ))}
                          </div>
                        </Td>
                      </Tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={jobsReports?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
